import { motion } from 'framer-motion'
import Navbar from '@/components/layout/Navbar'
import Footer from '@/components/layout/Footer'
import GCCJourneySection from '@/components/home/GCCJourneySection'
import FourFactorSection from '@/components/home/FourFactorSection'
import StatsSection from '@/components/home/StatsSection'
import FinalCTA from '@/components/home/FinalCTA'

const lineV = {
  hidden: { y: '115%' },
  visible: (i) => ({
    y: '0%',
    transition: { duration: 1.05, ease: [0.16, 1, 0.3, 1], delay: 0.05 + i * 0.1 },
  }),
}

const differentiators = [
  {
    num: '01',
    title: 'GCC talent is not standard recruitment',
    body: 'The founding leader of a GCC is not a hire. They are the centre\'s first operating decision. They shape every subsequent hire, set the employer brand before it exists, and navigate two very different organisations. Generic search firms miss this.',
  },
  {
    num: '02',
    title: 'Deep local market knowledge is non-negotiable',
    body: 'Compensation structures, title expectations, community networks, the reputational landscape — these are not learnable from a LinkedIn search. They require years of direct engagement with the talent market in Bengaluru, Hyderabad, Pune, and Mumbai.',
  },
  {
    num: '03',
    title: 'Speed without structure destroys quality',
    body: 'Most GCC talent failures are hiring failures that happened too fast. We slow down the brief interrogation to speed up the placement — because a well-defined search takes six weeks, not six months.',
  },
]

const stats = [
  { value: '1,800+', label: 'GCCs now operating globally', note: 'NASSCOM 2025 estimates' },
  { value: '₹3L Cr', label: 'Value added annually by GCCs', note: 'Government of India data' },
  { value: '50%', label: 'of Fortune 500 run global GCCs', note: 'Industry estimates' },
]

export default function GCCPracticePage() {
  return (
    <main data-testid="gcc-practice-page">
      <Navbar />

      {/* Hero */}
      <section className="min-h-[70vh] bg-[#0B1F3A] flex flex-col justify-end px-6 md:px-12 lg:px-24 pb-20 pt-36 relative overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute left-1/3 top-0 bottom-0 w-px bg-white" />
          <div className="absolute right-1/3 top-0 bottom-0 w-px bg-white" />
        </div>
        <motion.div initial="hidden" animate="visible" className="max-w-7xl relative z-10">
          <div className="overflow-hidden mb-6">
            <motion.p custom={0} variants={lineV}
              className="text-[10px] tracking-[0.38em] uppercase font-body font-semibold text-white/30">
              GCC Practice
            </motion.p>
          </div>
          <h1 className="mb-8">
            <div className="overflow-hidden pb-2">
              <motion.div custom={1} variants={lineV}
                className="text-[clamp(3rem,7vw,7.5rem)] font-heading font-black tracking-[-0.03em] leading-none text-white">
                From founding leader
              </motion.div>
            </div>
            <div className="overflow-hidden pb-2">
              <motion.div custom={2} variants={lineV}
                className="text-[clamp(3rem,7vw,7.5rem)] font-heading font-light tracking-[-0.03em] leading-none text-white/35">
                to operating centre.
              </motion.div>
            </div>
          </h1>
          <div className="overflow-hidden">
            <motion.p custom={3} variants={lineV}
              className="text-base md:text-lg text-white/45 max-w-xl font-body leading-relaxed">
              Over 1,800 Global Capability Centres are now operating worldwide. The ones that succeed share one thing: they hired better, earlier, and with more intent.
            </motion.p>
          </div>
        </motion.div>
      </section>

      {/* GCC context stats */}
      <section
        data-testid="gcc-context-section"
        className="py-20 px-6 md:px-12 lg:px-24 bg-white border-b border-[#E5E7EB]"
      >
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-0 divide-y md:divide-y-0 md:divide-x divide-[#E5E7EB]">
            {stats.map((stat, i) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.6, delay: i * 0.08 }}
                className="py-10 md:px-12 first:pl-0 last:pr-0"
                data-testid={`gcc-stat-${i + 1}`}
              >
                <p className="text-[clamp(2.5rem,5vw,4rem)] font-heading font-black text-[#0B1F3A] leading-none tracking-tighter mb-2">
                  {stat.value}
                </p>
                <p className="text-sm font-body font-semibold text-[#0A0A0A]/70 mb-1">{stat.label}</p>
                <p className="text-xs font-body text-[#0A0A0A]/30">{stat.note}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why GCC talent is different */}
      <section
        data-testid="gcc-differentiators-section"
        className="py-32 md:py-40 px-6 md:px-12 lg:px-24 bg-white"
      >
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="mb-20"
          >
            <p className="text-[10px] tracking-[0.35em] uppercase text-[#0A0A0A]/30 mb-5 font-body font-semibold">
              The Context
            </p>
            <h2 className="text-[clamp(2.5rem,5vw,5rem)] font-heading font-black tracking-tighter leading-none text-[#0A0A0A]">
              Why GCC talent<br />
              <span className="font-light text-[#16365F]">is different.</span>
            </h2>
          </motion.div>

          <div className="border-t border-[#E5E7EB]">
            {differentiators.map((d, i) => (
              <motion.div
                key={d.num}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1], delay: i * 0.07 }}
                className="py-12 border-b border-[#E5E7EB] grid grid-cols-1 md:grid-cols-[80px_1fr_2fr] gap-4 md:gap-10"
                data-testid={`gcc-diff-${i + 1}`}
              >
                <p className="text-[9px] tracking-[0.3em] uppercase text-[#0A0A0A]/20 font-body font-semibold pt-1">
                  {d.num}
                </p>
                <h3 className="text-lg font-heading font-black text-[#0A0A0A] leading-tight tracking-tight">
                  {d.title}
                </h3>
                <p className="text-sm font-body text-[#0A0A0A]/55 leading-relaxed">
                  {d.body}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <GCCJourneySection />
      <FourFactorSection />
      <StatsSection />
      <FinalCTA />
      <Footer />
    </main>
  )
}
